import { tx, txt, useContent } from "@/content/ContentContext";
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Header, Footer } from "@/pages/Landing";
import { api } from "@/lib/api";
import { BACKEND } from "@/lib/host";

const MEDIA = BACKEND;

const GROUPS = () => ([
  [tx("how.sign_up_in_a_minute"), tx("how.no_card_no_install_your"), ["signup"]],
  [tx("how.sell_on_any_screen"), tx("how.counter_pc_tablet_or_the"), ["pos_desktop", "pos_phone", "pos_phone_cart", "barcode_scan", "barcode_not_found"]],
  [tx("how.discounts_and_payment"), tx("how.line_and_cart_discounts_cash"), ["discount_line", "discount_cart", "pay_cash", "pay_card", "sale_complete"]],
  [tx("how.thermal_receipts"), tx("how.58mm_or_80mm_printed_from"), ["receipt_80", "receipt_58"]],
  [tx("how.restaurant_tables_to_kitchen"), tx("how.live_table_map_orders_from"), ["rest_tables", "rest_order_phone", "rest_kitchen"]],
  [tx("how.restaurant_bill_book_and_order"), tx("how.bill_with_discounts_and_free"), ["rest_bill", "rest_reservations", "rest_online_queue"]],
  [tx("how.pharmacy_fefo"), tx("how.the_earliest_expiring_valid_batch"), ["fefo_split", "fefo_blocked", "quickadd_pharmacy"]],
  [tx("how.online_store_premium"), tx("how.your_own_mobile_shop_customers"), ["store_front", "store_cart", "store_checkout", "store_track", "store_owner", "store_orders"]],
  [tx("how.sales_reports_premium"), tx("how.pick_a_date_range_group"), ["reports_month", "reports_weekly", "reports_phone"]],
  [tx("how.profit_loss_premium"), tx("how.every_sale_line_keeps_its"), ["pl_month", "pl_phone"]],
  [tx("how.returns_credit_notes"), tx("how.scan_the_receipt_barcode_return"), ["ret_receipt_barcode", "ret_lookup", "ret_conditions", "ret_cash_receipt", "ret_credit_note", "ret_cn_redeem"]],
  [tx("how.scan_with_your_phone_camera"), tx("how.tap_the_camera_next_to"), ["cam_overlay"]],
  [tx("how.worldwide_countries_tax"), tx("how.every_country_with_its_currency"), ["tax_signup", "tax_picker", "tax_us_state", "tax_canada"]],
  [tx("how.reseller_program"), tx("how.resellers_share_a_referral_link"), ["rs_landing", "rs_apply", "rs_performance", "rs_businesses", "rs_statement"]],
  [tx("how.goods_receiving_shelves"), tx("how.optional_inventory_add_on_receive"), ["inv_locations", "inv_grn_entry", "inv_grn_posted", "inv_putaway", "inv_stock", "inv_pick_pharmacy", "inv_pick_retail"]],
  [tx("how.products_and_sales_history"), tx("how.quick_add_products_and_reprint"), ["quickadd_retail", "sales_list", "sales_view"]],
  [tx("how.settings_branches_and_team"), tx("how.tax_by_country_printer_setup"), ["settings_top", "settings_receipt", "team_branches", "team_permissions", "branch_switcher", "cashier_view"]],
  [tx("how.demo_and_upgrade"), tx("how.15_days_free_a_clear"), ["demo_countdown", "lock_screen"]],
]);

function Shot({ s, onOpen }) {
  return (
    <figure className="group" data-testid={`how-shot-${s.id}`}>
      <button onClick={onOpen} className="relative block w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-[0_12px_30px_-18px_rgba(15,23,42,0.5)]" aria-label={`Enlarge ${s.title}`} data-testid={`how-shot-open-${s.id}`}>
        <img src={`${MEDIA}${s.thumb}`} alt={s.title} loading="lazy" decoding="async" width={640} height={Math.round((640 * s.height) / s.width)} className={`aspect-[16/10] w-full transition-transform duration-500 group-hover:scale-[1.02] ${s.height > s.width ? "bg-slate-900 object-contain" : "object-cover object-top"}`} />
        <span className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-lg bg-slate-950/70 text-white opacity-0 transition-opacity group-hover:opacity-100"><Expand className="h-4 w-4" /></span>
      </button>
      <figcaption className="mt-3">
        <div className="font-display font-bold text-slate-900">{s.title}</div>
        <p className="mt-0.5 text-sm leading-relaxed text-slate-600">{s.caption}</p>
      </figcaption>
    </figure>
  );
}

function Lightbox({ list, index, setIndex }) {
  const s = index === null ? null : list[index];
  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % list.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + list.length) % list.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, list.length, setIndex]);
  return (
    <Dialog open={!!s} onOpenChange={(o) => !o && setIndex(null)}>
      <DialogContent className="max-h-[96dvh] max-w-[min(96vw,1400px)] gap-3 overflow-y-auto border-0 bg-slate-950 p-3 text-white sm:p-5" data-testid="how-lightbox">
        {s && (<>
          <DialogTitle className="pr-8 font-display text-lg">{s.title}</DialogTitle>
          <DialogDescription className="text-sm text-slate-400">{s.caption}</DialogDescription>
          <img src={`${MEDIA}${s.image}`} alt={s.title} className="mx-auto max-h-[76dvh] w-auto rounded-lg" data-testid="how-lightbox-image" />
          <div className="flex items-center justify-between">
            <button onClick={() => setIndex((i) => (i - 1 + list.length) % list.length)} data-testid="how-lightbox-prev" className="flex h-10 items-center gap-1 rounded-lg px-3 text-sm font-semibold text-slate-300 hover:bg-white/10"><ChevronLeft className="h-4 w-4" /> {txt("how.prev")}</button>
            <span className="font-mono text-xs text-slate-500">{index + 1} / {list.length}</span>
            <button onClick={() => setIndex((i) => (i + 1) % list.length)} data-testid="how-lightbox-next" className="flex h-10 items-center gap-1 rounded-lg px-3 text-sm font-semibold text-slate-300 hover:bg-white/10">{txt("how.next")} <ChevronRight className="h-4 w-4" /></button>
          </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
}

export default function HowItWorks() {
  useContent();
  const [shots, setShots] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/how-it-works").then((r) => setShots(r.data)).catch(() => {});
  }, []);
  const byId = useMemo(() => Object.fromEntries(shots.map((s) => [s.id, s])), [shots]);
  const flat = useMemo(() => GROUPS().flatMap(([, , ids]) => ids.map((id) => byId[id]).filter(Boolean)), [byId]);

  return (
    <div className="bg-slate-50" data-testid="how-it-works-page">
      <Header />
      <section className="grain bg-slate-950 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="eyebrow !text-amber-400">{txt("how.how_it_works")}</div>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl">{txt("how.every_screen_exactly_as_your")}</h1>
          <p className="mt-4 max-w-2xl text-base text-slate-400">{txt("how.real_screenshots_from_the_live")}</p>
        </div>
      </section>
      <div className="mx-auto max-w-7xl space-y-20 px-4 py-16 sm:px-8">
        {shots.length === 0 && <div className="py-20 text-center text-slate-400" data-testid="how-loading">{txt("how.loading_walkthrough")}</div>}
        {shots.length > 0 && GROUPS().map(([title, sub, ids], gi) => (
          <section key={title} className="grid gap-8 lg:grid-cols-[280px_1fr]" data-testid={`how-step-group-${gi + 1}`}>
            <div className="lg:sticky lg:top-24 lg:self-start">
              <span className="money text-4xl font-extrabold text-amber-500">{String(gi + 1).padStart(2, "0")}</span>
              <h2 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-slate-900">{title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{sub}</p>
            </div>
            <div className="grid gap-8 sm:grid-cols-2">
              {ids.map((id) => byId[id] && <Shot key={id} s={byId[id]} onOpen={() => setOpen(flat.indexOf(byId[id]))} />)}
            </div>
          </section>
        ))}
      </div>
      <Footer />
      <Lightbox list={flat} index={open} setIndex={setOpen} />
    </div>
  );
}
